/**
 * `IEnvelopeCryptoAdapter` — consumer-side seam for substrate
 * envelope-encryption primitives.
 *
 * Per Tech Spec §6.2.3 + scope memo §5.1, the patient session performs
 * client-side wrap / unwrap / encrypt / decrypt against PMK / KEK /
 * DEK material that exists only transiently in active session memory.
 *
 * **ADR-032 §1 (Accepted, batch-3 ratification 2026-05-05).** The
 * substrate's canonical envelope-encryption primitives live in
 * `@shn/envelope-crypto` — isomorphic Web Crypto API surface that
 * runs in browser, React Native, and Node ≥ 20. PR #153 lifted the
 * `deriveKeyFromPassphrase` PBKDF2 derivation as a canonical
 * primitive shared across substrate-side test paths (DEV3 recovery
 * flow backend) and client-side patient-session paths (DEV5 item 2
 * auth + KEK release). The adapter pattern is preserved so the
 * eventual iOS shell can substitute a Swift-native implementation
 * (per the v1.5+/Pattern 3 framework-decision revisit trigger;
 * memory: `project_phg_client_framework_decision`).
 *
 * **Doctrinal posture: NO SHT BACKDOOR — structurally enforced.**
 *
 * Per the Web Crypto runtime, `CryptoKey` objects produced by
 * `unwrapKey` are `extractable: false` by default. The runtime
 * refuses to surface the underlying bytes via any API path —
 * `JSON.stringify`, `wrapKey` re-export, `localStorage` /
 * `sessionStorage` / `IndexedDB` persistence are all structurally
 * blocked. This is the load-bearing protection for the doctrinal
 * "cleartext keys live in active session memory only" commitment
 * per `feedback_phg_client_active_session_crypto.md`.
 *
 * **Why CryptoKey, not Uint8Array.** PR #39's adapter shape used
 * `Uint8Array` for keys — the wrong abstraction once Option A
 * ratified. Bytes-based keys are extractable by definition (the
 * caller already has them); session zeroize is best-effort defense
 * against in-memory recovery attacks. CryptoKey-based keys are
 * structurally non-extractable (the runtime refuses to surface
 * bytes), making the doctrinal property runtime-enforced rather
 * than discipline-enforced. PR #88 module commentary names this
 * shift explicitly.
 *
 * **Session lifecycle implications.** `ActiveSession.endSession()`
 * no longer needs to call `zeroizeBytes` on the KEK — there are no
 * KEK bytes for the session to hold. Dropping the `CryptoKey`
 * reference + GC reclamation is sufficient; the runtime never
 * exposed bytes in the first place. The `zeroizeBytes` helper
 * remains in the adapter for raw-bytes auth-factor cleanup
 * (passwords, mnemonics-in-progress) and recovery-share
 * recombination paths.
 */
import { EnvelopeCryptoError, KEY_LENGTH_BYTES, aeadDecrypt, aeadEncrypt, deriveKeyFromPassphrase as ecDeriveKeyFromPassphrase, generateRandomKey as ecGenerateRandomKey, unwrapKey as ecUnwrapKey, wrapKey as ecWrapKey, zeroizeBytes as ecZeroizeBytes, } from '@shn/envelope-crypto';
export class EnvelopeCryptoAdapterError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'EnvelopeCryptoAdapterError';
        this.code = code;
    }
}
// ============================================================================
// Default implementation — delegates to @shn/envelope-crypto
// ============================================================================
/**
 * Default adapter implementation. Delegates to `@shn/envelope-crypto`
 * for all primitive operations:
 *
 *   - `derivePMK` → `deriveKeyFromPassphrase` (canonical PBKDF2-SHA-256
 *     primitive lifted in PR #153 per ADR-032 §1)
 *   - `generateRandomKey` → `generateRandomKey` (CSPRNG; forwards
 *     `extractable` + `usages` for defense-in-depth narrowing)
 *   - `wrapKey` / `unwrapKey` → `wrapKey` / `unwrapKey` (AES-GCM AEAD
 *     wrap; `unwrapKey` forwards `extractable` + `usages` for
 *     defense-in-depth narrowing)
 *   - `encryptRecord` / `decryptRecord` → `aeadEncrypt` / `aeadDecrypt`
 *
 * Adapter remains the seam for iOS Swift-native substitution at
 * v1.5+ per Pattern 3 framework-decision revisit; this default
 * delegates to the isomorphic Web Crypto surface that ADR-032 §1
 * Accepted-status-ratifies.
 */
export class EnvelopeCryptoAdapter {
    /**
     * Delegates to `@shn/envelope-crypto`'s `deriveKeyFromPassphrase`
     * canonical primitive. The package surface is shared across substrate-
     * side test paths (DEV3 recovery flow backend; Issue #48 / Issue #140)
     * and client-side patient-session paths (DEV5 item 2 auth + KEK release;
     * recovery-passphrase derivation per scope memo §5.2). Lifting the
     * derivation into the canonical package per outgoing-DEV3's PR #107
     * follow-on observation #6: "`derivePMK` placement — should lift from
     * DEV5's adapter into `@shn/envelope-crypto` when item 3 (recovery)
     * starts."
     *
     * Adapter remains the seam for iOS Swift-native substitution at v1.5+
     * per Pattern 3 framework-decision revisit; this default delegates.
     */
    async derivePMK(params) {
        try {
            return await ecDeriveKeyFromPassphrase({
                passphrase: params.authFactor,
                salt: params.salt,
                ...(params.iterations !== undefined ? { iterations: params.iterations } : {}),
                usages: ['wrapKey', 'unwrapKey'],
            });
        }
        catch (e) {
            // Map canonical `EnvelopeCryptoError` into adapter-local code so
            // existing client callers' error-handling paths stay stable.
            if (e instanceof EnvelopeCryptoError) {
                throw new EnvelopeCryptoAdapterError(e.message, 'pmk-derivation-failed');
            }
            throw new EnvelopeCryptoAdapterError(`PMK derivation failed: ${e instanceof Error ? e.message : String(e)}`, 'pmk-derivation-failed');
        }
    }
    async generateRandomKey(opts) {
        return ecGenerateRandomKey(opts);
    }
    async wrapKey(opts) {
        try {
            return await ecWrapKey(opts.innerKey, opts.outerKey, opts.aad);
        }
        catch (e) {
            throw mapEnvelopeError(e);
        }
    }
    async unwrapKey(opts) {
        try {
            return await ecUnwrapKey(opts.wrappedBytes, opts.outerKey, opts.aad, {
                extractable: opts.extractable ?? false,
                ...(opts.usages !== undefined ? { usages: opts.usages } : {}),
            });
        }
        catch (e) {
            throw mapEnvelopeError(e);
        }
    }
    async encryptRecord(opts) {
        try {
            return await aeadEncrypt(opts.cleartext, opts.dek, opts.aad);
        }
        catch (e) {
            throw mapEnvelopeError(e);
        }
    }
    async decryptRecord(opts) {
        try {
            return await aeadDecrypt(opts.ciphertext, opts.dek, opts.aad);
        }
        catch (e) {
            throw mapEnvelopeError(e);
        }
    }
    zeroizeBytes(buf) {
        ecZeroizeBytes(buf);
    }
}
function mapEnvelopeError(e) {
    if (e instanceof EnvelopeCryptoError) {
        if (e.code === 'authentication-failed') {
            return new EnvelopeCryptoAdapterError(e.message, 'authentication-failed');
        }
        if (e.code === 'invalid-length') {
            return new EnvelopeCryptoAdapterError(e.message, 'invalid-length');
        }
        return new EnvelopeCryptoAdapterError(e.message, 'invalid-key');
    }
    return new EnvelopeCryptoAdapterError(e instanceof Error ? e.message : String(e), 'invalid-key');
}
// ============================================================================
// Re-exports for consumers
// ============================================================================
/** Re-export `KEY_LENGTH_BYTES` for callers constructing AAD bytes
 *  with key-length-derived sizes. */
export { KEY_LENGTH_BYTES };
/**
 * **DEPRECATED.** v0 transitional stub from PR #39 scaffold.
 * Retained as an alias of `EnvelopeCryptoAdapter` so existing
 * test imports still resolve; new code uses
 * `EnvelopeCryptoAdapter` directly.
 *
 * Per DEV1 review observation #2 on PR #39 (Option A/B/C JSDoc
 * drift): Option A is ratified + landed as `@shn/envelope-crypto`
 * (PR #88 cycle). The "not yet wired" framing is obsolete; the
 * adapter is now wired.
 */
export const NotYetWiredEnvelopeCryptoAdapter = EnvelopeCryptoAdapter;
//# sourceMappingURL=envelope-crypto-adapter.js.map